import { useCallback } from "react";
import {
  RETRYABLE_TITLE,
  copyReference,
  describeFailure,
  failureSentence,
  reportFailureShown,
} from "@/lib/feedback/failure";
import { useNotice, type NoticeOptions } from "@/lib/feedback/NoticeProvider";

/**
 * The notice's error path: `useApiErrorToast`'s logic moved onto the page notice,
 * with its `content` bug fixed (ux-s10.md §3; `use-api-error-toast.ts:27-30`).
 *
 * Only for an error whose control is gone (a row action, a menu item, a closed
 * dialog). An error from a control that is still on screen, including a dialog or
 * drawer that stays open, goes in an `InlineErrorBar` beside it instead.
 *
 * What changes against the toast: the message and "Reference id {id}" are both in
 * the notice's message (the toast put the id in `content`, which the library
 * renders INSTEAD of the message, so the server's sentence was never shown), and
 * "Copy reference" is the notice's action. The `retryable` "Try again" title and
 * the identifiers-only log line are kept as they were.
 *
 * Returns the notice id.
 */
export function useApiErrorNotice() {
  const notice = useNotice();

  return useCallback(
    (
      error: unknown,
      fallbackMessage = "Something went wrong.",
      options: Pick<NoticeOptions, "forPath" | "key" | "title"> = {},
    ): string => {
      const failure = describeFailure(error, fallbackMessage);
      // #1110 review: a cancelled step-up sign-in is the person's own choice, not a failure: a
      // warning with the screen's sentence, no failure title and no reference id.
      if (failure.cancelled) {
        return notice.warning(failure.message, { forPath: options.forPath, key: options.key });
      }
      reportFailureShown(error);
      const reference = failure.reference;
      return notice.error(failureSentence(failure), {
        ...options,
        // A retryable failure always reads "Try again"; otherwise the caller may name what failed.
        title: failure.retryable ? RETRYABLE_TITLE : options.title,
        action: reference
          ? {
              label: "Copy reference",
              doneLabel: "Copied",
              onClick: () => copyReference(reference),
            }
          : undefined,
      });
    },
    [notice],
  );
}
